import {Component, inject, Input} from '@angular/core';
import {ReportingService} from "../../../services/reporting.service";
import {IDateRangeReportingData} from "../../../interfaces/reporting/IDateRangeReportingData";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {IStandardGameReportPerDateRange} from "../../../interfaces/IStandardGameReportPerDateRange";
import {IGameInstanceSummaryReport} from "../../../interfaces/IGameInstanceSummaryReport";
import {
  ComponentLevelErrorMessageComponent
} from "../../display-errors/component-level-error-message/component-level-error-message.component";
import {ColDef, GridApi, GridReadyEvent} from "ag-grid-community";
import {FormatCurrencyPipe} from "../../../pipes/format-currency.pipe";
import {DatePipe} from "@angular/common";
import {AgGridAngular} from "ag-grid-angular";
import {MatButton} from "@angular/material/button";
import {UtilService} from "../../../services/util.service";
import {WinnerPayoutStatusTypes} from "../../../enum/WinnerPayoutStatusTypes";
import {WinnerPayoutStatusPipe} from "../../../pipes/winner-payout-status.pipe";
import {LoadingCardContentComponent} from "../../../shared/loading-card-content/loading-card-content.component";

@Component({
  selector: 'app-multiple-standard-game-reports',
  standalone: true,
  imports: [
    ComponentLevelErrorMessageComponent,
    AgGridAngular,
    DatePipe,
    MatButton,
    LoadingCardContentComponent
  ],
  templateUrl: './multiple-standard-game-reports.component.html',
  styleUrl: './multiple-standard-game-reports.component.css'
})
export class MultipleStandardGameReportsComponent {
  private reportingService: ReportingService = inject(ReportingService);
  private currencyPipe: FormatCurrencyPipe = inject(FormatCurrencyPipe);
  private utilService: UtilService = inject(UtilService);
  private datePipe: DatePipe = inject(DatePipe);
  private winnerPayoutStatusPipe: WinnerPayoutStatusPipe = inject(WinnerPayoutStatusPipe);

  @Input() set dateRangesSetter(valueP: IDateRangeReportingData) {
    this.errorMessage = '';
    this.uiState = UIStateEnum.ShowLoading;
    this.dateRanges = valueP;
    this.reports = [];
    let dateRangeParams: IStandardGameReportPerDateRange = {
      GameId: valueP.Game.Id,
      StartDate: new Date(valueP.StartDate),
      EndDate: new Date(valueP.EndDate)
    };

    this.reportingService.getMultipleStandardGameInstanceReports(dateRangeParams).subscribe({
      next: (reportsP) => {
        this.reports = reportsP;
        this.uiState = UIStateEnum.ShowData;
      },
      error: (err) => {
        console.log(err);
        this.errorMessage = 'Looks there is was an error attempting to retrieve multiple standard game reports. Please refresh and try again or contact a system administrator';
        this.uiState = UIStateEnum.ShowData;
      }
    })
  }

  public standardGameReportDefs: ColDef[] = [
    {
      headerName: 'Draw Date',
      resizable: true,
      sortable: true,
      field: 'EndDate',
      flex: 1,
      valueFormatter: params => {
        const converted = this.datePipe.transform(params.value, 'yyyy-MM-dd');
        return converted ? converted : '';
      }
    },
    {
      headerName: 'Played Tickets',
      resizable: true,
      sortable: true,
      field: 'PlayedTickets',
      flex: 1
    },
    {
      headerName: 'Gross Sales',
      resizable: true,
      sortable: true,
      field: 'GrossSales',
      flex: 1,
      valueFormatter: params => {
        const converted = this.currencyPipe.transform(params.value);
        return converted ? converted : '';
      }
    },
    {
      headerName: 'Winner Name',
      resizable: true,
      sortable: true,
      field: 'WinnerName',
      flex: 1
    },
    {
      headerName: 'Winner Played',
      resizable: true,
      sortable: true,
      field: 'WinnerPlayed',
      flex: 1,
    }
  ];
  public reports: IGameInstanceSummaryReport[] = [];
  uiState: UIStateEnum = UIStateEnum.ShowLoading;
  uiStateEnum = UIStateEnum;
  public errorMessage: string = '';
  multipleStandardGridApi!: GridApi;
  dateRanges!: IDateRangeReportingData;

  multipleStandardGridReady(params: GridReadyEvent<IGameInstanceSummaryReport>) {
    this.multipleStandardGridApi = params.api;
    this.multipleStandardGridApi.sizeColumnsToFit({
      defaultMinWidth: 90,
    });
  }

  downloadAsCSV() {
// Flatten each report into your existing format
    const formattedReports = this.reports.map(report => ({
      GameInstance: report.GameInstance,
      GameName: report.GameName,
      DrawTime: this.datePipe.transform(new Date(report.DrawTime), 'yyyy-MM-dd HH:mm'),
      EndDate: this.datePipe.transform(new Date(report.EndDate), 'yyyy-MM-dd HH:mm'),
      NotPlayedTickets: report.NotPlayedTickets,
      VoidedTickets: report.VoidedTickets,
      PlayedTickets: report.PlayedTickets,
      WinningNumber: report.WinningNumber,
      WinnerName: report.WinnerName,
      WinnerPlayed: report.WinnerPlayed ? 'Yes' : 'No',
      GrossSales: this.currencyPipe.transform(report.GrossSales),
      TotalPotContribution: this.currencyPipe.transform(report.TotalPotContribution),
      TotalCharityContribution: this.currencyPipe.transform(report.TotalCharityContribution),
      TotalAdministrationCommissionFees: this.currencyPipe.transform(report.TotalAdministrationCommissionFees),
      PreviousPotTotal: this.currencyPipe.transform(report.PreviousPotTotal),
      WinnerAmount: this.currencyPipe.transform(report.WinnerAmount),
      TotalStripeFees: this.currencyPipe.transform(report.TotalStripeFees),
      TotalCausableFees: this.currencyPipe.transform(report.TotalCausableFees),
      HstOnCausableFees: this.currencyPipe.transform(report.HstOnCausableFees),
      NumberOfRefundsIssued: report.NumberOfRefundedPayments,
      NumberOfRefundedTickets: report.NumberOfRefundedTickets,
      TotalRefundsDollarAmount: this.currencyPipe.transform(report.TotalRefundsDollarAmount),
      CharityRefundFees: report.TotalRefundsCharityContribution,
      NumberOfDisputes: report.NumberOfDisputedPayments,
      NumberOfDisputedTickets: report.NumberOfDisputedTickets,
      TotalDisputeDollarAmount: this.currencyPipe.transform(report.TotalDisputesDollarAmount),
      StripeDisputeFees: this.currencyPipe.transform(report.TotalStripeDisputeFees),
      CharityDisputeFees: this.currencyPipe.transform(report.TotalDisputeCharityContribution + report.TotalCharityDisputeFees),
      SeedTotal: this.currencyPipe.transform(report.SeedTotal),
      PotTotal: this.currencyPipe.transform(report.PotTotal),
      LicenceNumber: report.LicenceNumber,
      WinnerPayoutStatus: report.WinnerPayoutStatus ?
        this.winnerPayoutStatusPipe.transform(report.WinnerPayoutStatus as WinnerPayoutStatusTypes) : 'Winner did not play',
      ManualSales: this.currencyPipe.transform(report.ManualSales),
      AutoplaySales: this.currencyPipe.transform(report.AutoplaySales),
      TotalDisputeFees: this.currencyPipe.transform(report.TotalStripeDisputeFees + report.TotalCharityDisputeFees)
    }));

    // Trigger download
    let filename: string = `standard-game-reports-${this.datePipe.transform(this.dateRanges?.StartDate, 'yyyy-MM-dd')}-${this.datePipe.transform(this.dateRanges?.EndDate
      , 'yyyy-MM-dd')}`;

    this.utilService.downloadMultipleVerticalCSV(formattedReports, filename);
  }
}
